import type { ComponentType, ReactNode } from "react";
import { RouterProvider } from "react-router";
import { router } from "./routes";
import { CustomerDataAdapterProvider } from "@shared-core/data/CustomerDataAdapterContext";
import { CustomerLinkAdapterProvider } from "@shared-core/data/CustomerLinkAdapterContext";
import {
  PortalReviewScenariosProvider,
  PortalSignInAdapterProvider,
} from "@shared-core/data/PortalSignInAdapterContext";
import type {
  PortalReviewScenarios,
  PortalSignInAdapter,
} from "@shared-core/data/customer-portal-access-types";
import { PlatformCapabilitiesProvider } from "@shared-core/capabilities/context";
import { webCapabilities } from "../platform/capabilities";
import type { CustomerDataAdapter } from "@shared-core/data/customer-adapter";
import type { CustomerLinkAdapter } from "@shared-core/data/customer-link-adapter";
import { CustomerBasketAdapterProvider } from "@shared-core/data/CustomerBasketAdapterContext";
import type { CustomerBasketAdapter } from "@shared-core/data/customer-basket-adapter";
import type { MockBasketReview } from "@shared-core/mock-data/customer-portal-basket";
import { PortalBasketReviewContext } from "../pages/portal/PortalReviewContext";

export interface AppProps {
  dataAdapter: CustomerDataAdapter;
  /** The quote and order pages by their emailed link (public token routes). */
  linkAdapter: CustomerLinkAdapter;
  /**
   * What the server decided about the signed-in person and which customer they are working as
   * (first visit, chooser, no access, blocked, or straight in). The real adapter in the real
   * build, its mock twin in the mock build.
   */
  signInAdapter: PortalSignInAdapter;
  /**
   * The mock build's review tool: the sign-in states to jump between. The real build passes
   * nothing and has no review tool.
   */
  reviewScenarios?: PortalReviewScenarios;
  /**
   * The mock build's persona switcher, wrapped around everything (mock/MockPortalReviewRoot).
   * Passed in, not imported, so the real build's bundle never contains it.
   */
  reviewRoot?: ComponentType<{ children: ReactNode }>;
  /**
   * The basket and checkout pages' data. Absent (the staff view, a build with no basket) hides the
   * Basket nav item, Add to basket and Order again everywhere. The real build passes one that reads the basket
   * and says it cannot yet change it (every `capabilities` group false), which hides every control that would.
   */
  basketAdapter?: CustomerBasketAdapter;
  /** The mock build's basket review tool (puts a basket into a named state). The real build passes nothing. */
  basketReview?: MockBasketReview;
}

/**
 * App shell for the customer portal. `main.tsx` owns the mode selection
 * (mock vs real, mirroring apps/frontend/src/main.tsx's shape) and hands
 * the already-resolved adapters to this component.
 *
 * Who is signed in is whoever the token says: `PortalLayout`'s landing gate asks the server,
 * through `signInAdapter`, before any page renders.
 */
export function App({
  dataAdapter,
  linkAdapter,
  signInAdapter,
  reviewScenarios,
  reviewRoot: ReviewRoot,
  basketAdapter,
  basketReview,
}: AppProps) {
  let content: ReactNode = <RouterProvider router={router} />;
  if (reviewScenarios) {
    content = (
      <PortalReviewScenariosProvider scenarios={reviewScenarios}>
        {content}
      </PortalReviewScenariosProvider>
    );
  }
  if (basketReview) {
    content = (
      <PortalBasketReviewContext.Provider value={basketReview}>
        {content}
      </PortalBasketReviewContext.Provider>
    );
  }
  if (ReviewRoot) content = <ReviewRoot>{content}</ReviewRoot>;
  return (
    <PlatformCapabilitiesProvider capabilities={webCapabilities}>
      <CustomerDataAdapterProvider adapter={dataAdapter}>
        <CustomerLinkAdapterProvider adapter={linkAdapter}>
          <PortalSignInAdapterProvider adapter={signInAdapter}>
            <CustomerBasketAdapterProvider adapter={basketAdapter ?? null}>
              {content}
            </CustomerBasketAdapterProvider>
          </PortalSignInAdapterProvider>
        </CustomerLinkAdapterProvider>
      </CustomerDataAdapterProvider>
    </PlatformCapabilitiesProvider>
  );
}
